import type { ReactNode } from "react";

// ---------------------------------------------------------------------------
// Input block — the top elevated card
// ---------------------------------------------------------------------------

interface TokenInputBlockProps {
  /** Token symbol or "USD" shown on the right */
  symbol: string;
  amount: string;
  onAmountChange: (v: string) => void;
  /** Formatted balance number only (no ticker) */
  walletBalance?: string;
  onFillBalance?: () => void;
  onFillMin?: () => void;
  onFillMax?: () => void;
  /** Optional node rendered overlapping the top edge (e.g. asset selector) */
  badge?: ReactNode;
}

export function TokenInputBlock({
  symbol,
  amount,
  onAmountChange,
  walletBalance,
  onFillBalance,
  onFillMin,
  onFillMax,
  badge,
}: TokenInputBlockProps) {
  return (
    <div className="relative z-10 w-full rounded-2xl px-4 pb-3 md:pb-4 pt-10 md:pt-13 border border-white/10" style={{ background: "#27384b" }}>
      {/* Asset badge — absolute, centered on the top edge */}
      {badge && (
        <div className="absolute -top-6 left-1/2 -translate-x-1/2 z-30">
          {badge}
        </div>
      )}

      {/* Amount row */}
      <div className="flex items-baseline gap-2 min-w-0">
        <input
          type="text"
          inputMode="decimal"
          placeholder="0"
          value={amount}
          onChange={(e) => onAmountChange(e.target.value)}
          className="text-3xl md:text-4xl font-heading bg-transparent outline-none text-ghost-white flex-1 min-w-0 placeholder:text-ash/30"
        />
        <span className="text-xl font-heading text-ghost-white font-semibold shrink-0 leading-none">{symbol}</span>
      </div>

      {/* Balance + Min / Max */}
      <div className="flex items-center justify-between mt-2">
        <div>
          {walletBalance && (
            <button
              type="button"
              onClick={onFillBalance}
              className="flex items-center gap-1 text-[0.65rem] font-heading text-ghost-white hover:text-ghost-white transition-colors"
              title="Fill from wallet balance"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="shrink-0">
                <rect x="1" y="3" width="10" height="7.5" rx="1.5" stroke="currentColor" strokeWidth="1.1" />
                <path d="M1 5H11" stroke="currentColor" strokeWidth="1.1" />
                <circle cx="8.5" cy="7" r="0.75" fill="currentColor" />
              </svg>
              <span>{walletBalance}</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {onFillMin && (
            <button
              type="button"
              onClick={onFillMin}
              className="px-2.5 py-1 text-[0.65rem] font-heading font-medium rounded-lg bg-[#304257] text-[#5d6a7b] hover:text-ghost-white hover:bg-[#3d5068] transition-colors"
            >
              Min
            </button>
          )}
          {onFillMax && (
            <button
              type="button"
              onClick={onFillMax}
              className="px-2.5 py-1 text-[0.65rem] font-heading font-medium rounded-lg bg-[#304257] text-[#5d6a7b] hover:text-ghost-white hover:bg-[#3d5068] transition-colors"
            >
              Max
            </button>
          )}
        </div>
      </div>

    </div>
  );
}

// ---------------------------------------------------------------------------
// Display block — the bottom "behind" card (read-only equivalent value)
// ---------------------------------------------------------------------------

interface TokenDisplayBlockProps {
  value: string;
  unit: string;
  prefix?: string;
}

export function TokenDisplayBlock({
  value,
  unit,
  prefix = "≈",
}: TokenDisplayBlockProps) {
  return (
    <div
      className="relative z-0 w-full rounded-t-none rounded-b-2xl px-4 pt-6 md:pt-9 pb-3 md:pb-4 flex items-center justify-between border border-t-0 border-white/10"
      style={{ background: "#0e1a2a" }}
    >
      <div className="flex items-baseline gap-1.5">
        <span className="text-sm font-heading text-ghost-white/40">{prefix}</span>
        <span className="text-xl md:text-2xl font-heading text-ghost-white/50">{value || "0"}</span>
      </div>
      <span className="text-sm font-heading text-ghost-white/50 font-semibold">{unit}</span>
    </div>
  );
}
